import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import CheckIcon from "@mui/icons-material/Check";
import { InsightsMinibarDropdown } from "../../../interfaces";
import { HandleDataLayer } from "../../../Helpers";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    boxShadow: "0px 8px 12px #00000019",
    border: "1px solid #E8E8E8",
    borderRadius: 8,
  },
  menuList: {
    width: 278,
    padding: "12px",
    "& li": {
      color: "#747474",
      fontWeight: "500",
      padding: "8px 12px",
    },
  },
  classTitle: {
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    // whiteSpace: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  button: {
    position: "relative",
    padding: "0px 32px",
    width: "100%",
    backgroundColor: "transparent",
    borderRadius: "8px",
    color: theme.palette.text.primary,
    height: "87px",
    boxShadow: "none",
    fontSize: "28px",
    whiteSpace: "normal",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      height: "87px",
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  chevronIcon: {
    fontSize: "32px",
    color: theme.palette.text.secondary,
    transition: "transform 0.2s ease-in-out",
  },
  chevronIconDown: {
    "&:hover": {
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  chevronIconUp: {
    transform: "rotate(180deg)",
    "&:hover": {
      color: theme.palette.text.primary,
      boxShadow: "none",
      transform: "none",
    },
  },
  selectedMenuItemText: {
    font: "normal normal bold 16px/20px Roboto !important",
    color: "#191919 !important",
    whiteSpace: "normal",
  },
  menuItemText: {
    font: "normal normal 500 16px/20px Roboto !important",
    whiteSpace: "normal",
  },
}));

interface InsightsSelectorDropdownProps {
  dropDownItems: InsightsMinibarDropdown[];
  selectedItem: InsightsMinibarDropdown | null;
  selectedItemIndex: number | null;
  onChange: (item: InsightsMinibarDropdown) => void;
  sx?: any;
  disableDropdown?: boolean;
  dropDownMenuOpen: boolean;
}

interface dropdownItemProps {
  selected: boolean;
  onClick: () => void;
  item: any;
}

const DropDownItem = (props: dropdownItemProps) => {
  const { selected, onClick, item } = props;
  const classes = useStyles();
  const [hover, setHover] = useState(false);
  return (
    <MenuItem
      selected={selected}
      key={item.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}>
      <ListItemIcon>
        {selected ? <CheckIcon fontSize="small" color={"primary"} /> : <></>}
      </ListItemIcon>
      <ListItemText>
        <Typography
          component={"span"}
          variant={"body1"}
          className={
            hover || selected ? classes.selectedMenuItemText : classes.menuItemText
          }>
          {item.label}
        </Typography>
      </ListItemText>
    </MenuItem>
  );
};

export const InsightsSelectorDropdown = (props: InsightsSelectorDropdownProps) => {
  const classes = useStyles();
  const {
    selectedItem,
    onChange,
    sx,
    dropDownItems,
    disableDropdown = false,
    dropDownMenuOpen,
  } = props;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [mainMenuOpen, setMainMenuOpen] = useState(dropDownMenuOpen);
  const [availableItems, setAvailableItems] = useState<InsightsMinibarDropdown[]>([]);
  const [itemSelected, setItemSelected] = useState(
    selectedItem ?? ({} as InsightsMinibarDropdown)
  );

  useEffect(() => {
    setAvailableItems(dropDownItems);
  }, [dropDownItems]);

  useEffect(() => {
    setItemSelected(selectedItem ?? ({} as InsightsMinibarDropdown));
  }, [selectedItem]);

  useEffect(() => {
    setMainMenuOpen(dropDownMenuOpen);
  }, [dropDownMenuOpen]);

  return (
    <>
      {!disableDropdown ? (
        <Box sx={sx}>
          <Button
            className={classes.button}
            variant="contained"
            disableElevation
            ref={buttonRef}
            onClick={() => {
              HandleDataLayer(
                "collapse",
                "open",
                "click",
                "navbar",
                "sidebar",
                `${itemSelected.label}`,
                "onclick"
              );
              setMainMenuOpen(true);
            }}>
            <Stack
              direction="row"
              alignItems="center"
              style={{ width: "100%", height: "100%" }}>
              <Box
                style={{ flexGrow: 1, maxHeight: "100%" }}
                className={classes.classTitle}>
                {itemSelected.label}
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                style={{ height: "100%", marginLeft: "auto" }}>
                <i
                  className={`far fa-chevron-down ${classes.chevronIcon} ${
                    mainMenuOpen ? classes.chevronIconUp : classes.chevronIconDown
                  }`}
                />
              </Stack>
            </Stack>
          </Button>
          <Menu
            anchorEl={buttonRef.current}
            open={mainMenuOpen}
            onClose={() => {
              HandleDataLayer(
                "collapse",
                "close",
                "click",
                "navbar",
                "sidebar",
                `${itemSelected.label}`,
                "onclick"
              );
              setMainMenuOpen(false);
            }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: -8 }}
            MenuListProps={{ className: classes.menuList }}
            PaperProps={{ className: classes.menuPaper }}>
            {availableItems.map((item) => {
              let selected = itemSelected.id === item.id;
              return (
                <DropDownItem
                  key={item.id}
                  item={item}
                  onClick={() => {
                    setItemSelected(item);
                    setMainMenuOpen(false);
                    onChange(item);
                  }}
                  selected={selected}
                />
              );
            })}
          </Menu>
        </Box>
      ) : (
        <Typography
          variant={"body1"}
          sx={{
            textWrap: "initial",
            color: "#191919",
            font: "normal normal 600 20px/27px proxima-soft",
            letterSpacing: "0.15px",
            textAlign: "left",
          }}>
          {itemSelected.label}
        </Typography>
      )}
    </>
  );
};
